import { client } from '@/client';
import { Campaign, CampaignFormValues } from '@/client/campaigns';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { ExcludeFormValue, TargetFormValue } from '@/common/types';
import { FormikSwitch } from '@/components/form';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { TargetEntitiesSelector } from '@/ui/target-selector';
import {
  getPhishingLanguageCode,
  mapLanguageToPhishingLanguage,
} from '@/utils/helpers';
import { WarningTriangleIcon } from '@/utils/helpers/icons.helper';
import { getTargetsAndExcludes } from '@/utils/targets';
import { getAppAbsoluteUrl } from '@/utils/url';
import { Field } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledUnverifiedDomainWarning = styled.div`
  width: 100%;
  background-color: var(--yellow-pale);
  border-radius: var(--small-border-radius);
  padding: var(--default-padding-small) var(--default-padding);
  padding-left: var(--default-padding-small);
  margin-top: var(--medium-padding);
  display: flex;
  gap: var(--medium-padding);
  align-items: flex-start;

  svg {
    flex-shrink: 0;
  }

  .warning-text {
    display: flex;
    flex-direction: column;
    gap: var(--small-padding);

    p {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      font-weight: 400;
      margin: 0;
    }

    .warning-link {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      font-weight: 400;
      color: var(--black-main);
      cursor: pointer;
      text-decoration: none;
      border-bottom: 2px solid var(--gray-buttom-border);
      background: none;
      padding-bottom: 6px;
    }
  }
`;

type TargetGroupStepProps = {
  values: CampaignFormValues;
  valuesSetter: (field: string, value: any) => void;
  campaign?: Campaign;
};

// The language a send created with language targeting goes out in, or null for other campaigns.
const getTargetLanguage = (campaign?: Campaign) =>
  (campaign?.languageGroupId || campaign?.languageTargetingMethod) &&
  campaign.language
    ? mapLanguageToPhishingLanguage(campaign.language)
    : null;

export const TargetGroupStep: React.FC<TargetGroupStepProps> = ({
  values,
  valuesSetter,
  campaign,
}) => {
  const { t } = useTranslation();
  const account = useAppSelector(selectCurrentAccount);
  const targetLanguage = getTargetLanguage(campaign);

  const [excludedDomainUnverifiedCount, setExcludedDomainUnverifiedCount] =
    useState<number>(0);

  const targetsKey = (values.targets ?? []).map((t) => t.id).join(',');
  const excludesKey = (values.excludes ?? []).map((e) => e.id).join(',');
  useEffect(() => {
    if (!account?.id) return;
    client.planner
      .targetSelectorPreview(
        {
          ...getTargetsAndExcludes(values.targets ?? [], values.excludes ?? []),
          domainVerifiedOnly: true,
        },
        account.id,
      )
      .then((res) =>
        setExcludedDomainUnverifiedCount(res.excludedDomainUnverifiedCount),
      )
      .catch(() => setExcludedDomainUnverifiedCount(0));
  }, [targetsKey, excludesKey, account?.id]);

  return (
    <>
      <h2>{t('campaign.select.target')}</h2>

      <TargetEntitiesSelector
        className="phishing"
        title={t('campaign.target')}
        required
        includedEntities={values?.targets ?? []}
        setIncludedEntities={(values: TargetFormValue[]) =>
          valuesSetter('targets', values)
        }
        includeLabelSuffix={
          targetLanguage ? getPhishingLanguageCode(targetLanguage) : undefined
        }
        excludedEntities={values?.excludes ?? []}
        setExcludedEntities={(values: ExcludeFormValue[]) =>
          valuesSetter('excludes', values)
        }
      />
      {excludedDomainUnverifiedCount > 0 && (
        <StyledUnverifiedDomainWarning>
          <WarningTriangleIcon />
          <div className="warning-text">
            <p>
              <strong>
                {t('campaign.target.domainUnverified.count', {
                  count: excludedDomainUnverifiedCount,
                })}
              </strong>{' '}
              {t('campaign.target.domainUnverified.description')}
            </p>
            <div>
              <a
                target="_blank"
                className="warning-link"
                onClick={() =>
                  window.open(
                    getAppAbsoluteUrl(
                      RedirectPaths[
                        RedirectPathsEnum.ACCOUNT_EMAILS_AND_DELIVERY_DOMAINS
                      ](),
                    ),
                    '_blank',
                  )
                }
              >
                {t('campaign.target.domainUnverified.link')}
              </a>
            </div>
          </div>
        </StyledUnverifiedDomainWarning>
      )}
      <div className="field-checkbox mt-4">
        <Field
          inputId="useDynamicTarget"
          name="useDynamicTarget"
          label={t('campaign.dynamicTarget.enable')}
          component={FormikSwitch}
        />
      </div>
    </>
  );
};
